import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Shield, Lock, Eye, Database, UserCheck, Ban,
  CheckCircle, Info, ExternalLink, Heart, FileText
} from "lucide-react";

/**
 * Plain English Privacy Policy Summary
 * Builds trust during a vulnerable time by explaining data protection clearly
 */
export default function PrivacyPolicySummary({ showFull = false }) {
  const [showFullPolicy, setShowFullPolicy] = useState(showFull);

  return (
    <>
      {/* Summary Card */}
      <Card className="border-0 shadow-md bg-gradient-to-br from-white to-[#F0F7F4]" data-testid="privacy-summary">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-[#4A7C6F] flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg text-slate-800">Your Privacy Matters</CardTitle>
              <CardDescription>How we protect your information</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Key Points in Plain English */}
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#6B9B8A]/20">
              <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <Lock className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm">Your Data is Encrypted</h4>
                <p className="text-sm text-slate-600">
                  Everything you share with us is protected using the same security that banks use. 
                  Your health information is scrambled so only authorized people can read it.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#6B9B8A]/20">
              <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                <Ban className="w-4 h-4 text-rose-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm">We Never Sell Your Data</h4>
                <p className="text-sm text-slate-600">
                  Your personal and medical information will <strong>never</strong> be sold to 
                  advertisers, data brokers, or any third parties. Period.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#6B9B8A]/20">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Eye className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm">You Control Your Information</h4>
                <p className="text-sm text-slate-600">
                  You can see what we have, update it, or ask us to delete it at any time. 
                  Your care team only sees what they need to help you.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-[#6B9B8A]/20">
              <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <UserCheck className="w-4 h-4 text-purple-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-800 text-sm">HIPAA Protected</h4>
                <p className="text-sm text-slate-600">
                  We follow all federal health privacy laws (HIPAA). Your medical information 
                  is treated with the highest level of confidentiality.
                </p>
              </div>
            </div>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-2 py-3">
            <Badge className="bg-[#E8F4F0] text-[#4A7C6F] gap-1">
              <Shield className="w-3 h-3" />
              HIPAA Compliant
            </Badge>
            <Badge className="bg-[#E8F4F0] text-[#4A7C6F] gap-1">
              <Lock className="w-3 h-3" />
              256-bit Encryption
            </Badge>
          </div>

          {/* Read Full Policy */}
          <div className="pt-2 border-t">
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowFullPolicy(true)}
            >
              <FileText className="w-4 h-4 mr-2" />
              Read Full Privacy Policy
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Full Policy Dialog */}
      <Dialog open={showFullPolicy} onOpenChange={setShowFullPolicy}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#4A7C6F]" />
              Privacy Policy
            </DialogTitle>
            <DialogDescription>
              Goodwin Hospice — Last updated: January 2025
            </DialogDescription>
          </DialogHeader>
          
          <ScrollArea className="h-[50vh] pr-4">
            <div className="space-y-6 text-sm">
              <section>
                <h3 className="font-semibold text-slate-800 mb-2">What Information We Collect</h3>
                <p className="text-slate-600 mb-2">
                  When you use Goodwin Hospice services, we may collect:
                </p>
                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                  <li>Contact information (name, email, phone number)</li>
                  <li>Health information related to hospice care</li>
                  <li>Information you share with our AI assistant</li>
                  <li>Journal entries and care observations</li>
                  <li>Usage data to improve our services</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-slate-800 mb-2">How We Protect Your Data</h3>
                <p className="text-slate-600 mb-2">
                  Your security is our priority:
                </p>
                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                  <li><strong>Encryption:</strong> All data is encrypted using 256-bit AES encryption, both in transit and at rest</li>
                  <li><strong>Access Controls:</strong> Only authorized healthcare team members can access your information</li>
                  <li><strong>Regular Audits:</strong> We conduct security audits and monitor for unauthorized access</li>
                  <li><strong>Secure Servers:</strong> Data is stored in HIPAA-compliant, SOC 2 certified data centers</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-slate-800 mb-2">We Will Never...</h3>
                <div className="bg-rose-50 p-4 rounded-lg border border-rose-200">
                  <ul className="space-y-2 text-slate-700">
                    <li className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-rose-500" />
                      Sell your personal or health information to anyone
                    </li>
                    <li className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-rose-500" />
                      Share your data with advertisers or marketing companies
                    </li>
                    <li className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-rose-500" />
                      Use your information to deny you care or services
                    </li>
                    <li className="flex items-center gap-2">
                      <Ban className="w-4 h-4 text-rose-500" />
                      Keep your data longer than necessary
                    </li>
                  </ul>
                </div>
              </section>

              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Your Rights</h3>
                <p className="text-slate-600 mb-2">
                  You have the right to:
                </p>
                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                  <li>Access your personal and health information</li>
                  <li>Request corrections to inaccurate data</li>
                  <li>Request deletion of your data (with some exceptions)</li>
                  <li>Receive a copy of your data</li>
                  <li>Know who has accessed your information</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-slate-800 mb-2">HIPAA Compliance</h3>
                <p className="text-slate-600">
                  Goodwin Hospice complies with the Health Insurance Portability and 
                  Accountability Act (HIPAA). This means your Protected Health Information 
                  (PHI) is handled according to federal law, ensuring:
                </p>
                <ul className="list-disc pl-5 text-slate-600 space-y-1 mt-2">
                  <li>Privacy of your health records</li>
                  <li>Security safeguards for electronic health information</li>
                  <li>Notification if a breach ever occurs</li>
                  <li>Your rights to your health information</li>
                </ul>
              </section>

              <section>
                <h3 className="font-semibold text-slate-800 mb-2">Contact Us</h3>
                <p className="text-slate-600">
                  If you have questions about your privacy or want to exercise your rights:
                </p>
                <div className="bg-slate-50 p-4 rounded-lg mt-2">
                  <p className="text-slate-700"><strong>Privacy Officer</strong></p>
                  <p className="text-slate-600">Goodwin Hospice</p>
                  <p className="text-slate-600">Phone: 703-578-7108</p>
                  <p className="text-slate-600">Email: privacy@goodwinhospice.com</p>
                </div>
              </section>
            </div>
          </ScrollArea>

          <DialogFooter>
            <Button onClick={() => setShowFullPolicy(false)} className="bg-[#4A7C6F] hover:bg-[#3D6B5F]">
              I Understand
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Compact Privacy Badge for Footers/Headers
 */
export function PrivacyBadge({ onClick }) {
  return (
    <button 
      onClick={onClick}
      className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E8F4F0] text-[#4A7C6F] rounded-full text-sm hover:bg-[#D4EBE4] transition-colors"
      data-testid="privacy-badge"
    >
      <Shield className="w-4 h-4" />
      <span>Your Privacy Protected</span>
    </button>
  );
}
